import React, { useState, useEffect, useContext } from 'react'
import { toast } from 'react-toastify'
import { IdentityContext } from '#src/containers/Identity'
import { useExperiment } from '@deal/experiment-js'
import { Modal, Button, Image } from '@deal/components'
import {
  EngagementChannelCategoryFragment,
  RecommendExpert2Component,
  StartExpertConsumerConversationComponent,
  ContactType
} from '#src/generated/graphql'
import { SMSModalSchema } from '../schema'
import { setLandingPageKey } from '#src/services/registrationValues'
import { isValidPhoneNumber } from '#src/services/phone'
import { Stage } from '../'
import { AnalyticsContext } from '#src/containers/Analytics'
import config from '#src/config'
import cookies from 'js-cookie'
import InternationalPhoneField from '#src/components/InternationalPhoneField'
import StandaloneRegister from '#src/components/StandaloneRegister'
import ContentfulQuery from '#src/containers/Contentful'
import Logger from '#src/services/logger'
import Icon from './sms-sent.svg'
import styles from './styles.css'

interface Props {
  category: EngagementChannelCategoryFragment
  isModalOpen: boolean
  stage: Stage
  setIsModalOpen: (value: boolean) => void
  handleStage: (value: Stage) => void
  onDismissClick: (variation: string, categorySlug: string) => void
}

const V1: React.SFC<Props> = ({
  category,
  isModalOpen,
  stage,
  setIsModalOpen,
  handleStage,
  onDismissClick
}) => {
  if (!isModalOpen) return null

  const { result, loading: experimentLoading } = useExperiment({
    experiment: `sms-modal-${category.slug}`,
    defaultTreatment: ''
  })

  const { myself, refetch: refetchIdentity } = useContext(IdentityContext)
  const analytics = useContext(AnalyticsContext)

  const [phoneNumber, setPhoneNumber] = useState('')
  const [isPhoneValid, setIsPhoneValid] = useState(false)

  let contentfulVariation = `sms-modal-${category.slug}`
  if (result && result !== 'control' && result !== 'off') {
    contentfulVariation = result
  }

  useEffect(() => {
    setIsPhoneValid(isValidPhoneNumber(phoneNumber))
  }, [phoneNumber])

  useEffect(() => {
    if (result) {
      analytics?.events.engagementChannelViewed({
        engagement_channel: 'sms-modal',
        variation: contentfulVariation,
        category: category.slug
      })
    }
  }, [result])

  if (experimentLoading) {
    return null
  }

  return (
    <ContentfulQuery template="smsModal" variation={contentfulVariation}>
      {({ content, loading }: { content: SMSModalSchema; loading: boolean }) => {
        setLandingPageKey(contentfulVariation)

        if (content) {
          const loggedInPhone =
            myself && myself.user.contacts.find(({ type }) => type === ContactType.PHONE)
          return (
            <Modal isOpen={isModalOpen}>
              <>
                {category.contentAssets.fullPageTakeover &&
                  category.contentAssets.fullPageTakeover.imageUrl && (
                    <Image
                      src={category.contentAssets.fullPageTakeover.imageUrl}
                      width={636}
                      height={160}
                      className={styles.headerImage}
                    />
                  )}
                <div className={styles.container}>
                  <div className={styles.content}>
                    <div className={styles.title}>{content.title}</div>
                    {stage === Stage.Success && myself ? (
                      <RecommendExpert2Component
                        variables={{ categoryId: category.id, consumerId: myself.id }}
                      >
                        {({ data, loading }) => {
                          const expertId =
                            data &&
                            data.recommendExpert2 &&
                            data.recommendExpert2.expert &&
                            data.recommendExpert2.expert.id

                          // Prevent to show an unnecessary loader, just block the button button
                          if (loading || expertId) {
                            return (
                              <StartExpertConsumerConversationComponent>
                                {startExpertConsumerConversation => (
                                  <>
                                    {loggedInPhone && (
                                      <div className={styles.subtitleSuccess}>
                                        Success! Coupon code sent to {loggedInPhone.value}.
                                      </div>
                                    )}
                                    <Icon className={styles.icon} />
                                    <div className={styles.buttonContainer}>
                                      <Button
                                        onClick={() => {
                                          if (expertId) {
                                            startExpertConsumerConversation({
                                              variables: {
                                                input: {
                                                  consumerId: myself.id,
                                                  expertId
                                                }
                                              }
                                            })
                                            setIsModalOpen(false)
                                          }
                                        }}
                                        disabled={loading}
                                      >
                                        {content.completeButtonCopy}
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </StartExpertConsumerConversationComponent>
                            )
                          }
                          throw new Error('RecommendExpert2 return undefined')
                        }}
                      </RecommendExpert2Component>
                    ) : (
                      <>
                        <div className={styles.subtitle}>{content.bodyText}</div>
                        <div className={styles.buttonContainer}>
                          {stage === Stage.Initial && (
                            <Button
                              className={styles.button}
                              size="large"
                              onClick={() => {
                                handleStage(Stage.Typing)
                              }}
                            >
                              {content.initialButtonCopy}
                            </Button>
                          )}
                          {stage === Stage.Typing && (
                            <StandaloneRegister
                              onCompleted={() => {
                                // Set a cookie to allow cathing the modal after the refetch
                                cookies.set('sms_modal_registered', 'true', {
                                  // Expire in 1 minute
                                  expires: new Date(new Date().getTime() + 1 * 60 * 1000),
                                  domain: config.get('consumer.clientSideCookieDomain')
                                })

                                if (refetchIdentity) {
                                  refetchIdentity().then(result => {
                                    const refetchedMyself = result.data.me
                                    if (refetchedMyself) {
                                      analytics?.events.userRegistered({
                                        user_id: refetchedMyself.id,
                                        context: 'sms-modal'
                                      })
                                    }
                                  })
                                }
                              }}
                              input={{ phone: phoneNumber }}
                              handleRegistrationErrors={errors => toast.error(errors)}
                            >
                              {(registerCustomer, { loading }) => (
                                <div className={styles.inputContainer}>
                                  <div className={styles.input}>
                                    <InternationalPhoneField
                                      label=""
                                      labelHidden
                                      defaultValue={phoneNumber}
                                      valid={isPhoneValid}
                                      onChange={phoneNumber => {
                                        setPhoneNumber(phoneNumber)
                                      }}
                                    />
                                  </div>
                                  <Button
                                    disabled={loading || !isPhoneValid}
                                    onClick={() => {
                                      analytics?.events.engagementChannelSubmitted({
                                        engagement_channel: 'sms-modal',
                                        variation: 'v1',
                                        category: category.slug
                                      })

                                      registerCustomer()
                                    }}
                                  >
                                    {content.phoneButtonCopy}
                                  </Button>
                                </div>
                              )}
                            </StandaloneRegister>
                          )}
                          <Button
                            className={styles.button}
                            size="large"
                            variant="contrast-outline"
                            onClick={() => onDismissClick('v1', category.slug)}
                          >
                            {content.dismissButtonCopy}
                          </Button>
                        </div>
                        <div className={styles.mutedText}>
                          By submitting, you authorize Curated.com to send you automated text
                          messages to alert you of deals and activity from your expert. Message and
                          data rates may apply. You can turn off text alerts anytime by replying
                          STOP.
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            </Modal>
          )
        } else if (loading) {
          return null
        }

        Logger.captureException('No contentful data for sms modal', {
          slug: contentfulVariation
        })

        return null
      }}
    </ContentfulQuery>
  )
}

export default V1
